import React, { useState } from 'react';
import { Button, ButtonGroup, Pagination, Skeleton } from '@heroui/react';
import { useNotifications, useUnreadNotifications } from '@/entities/notification';
import { useNotificationActions, NotificationItem } from '@/features/notification-actions';

export const NotificationsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // 독립적인 카운트 쿼리 (최소 fetch)
  const { data: allCountData } = useNotifications({ page: 0, size: 1 });
  const { data: unreadCountData } = useUnreadNotifications({ page: 0, size: 1 });

  // 현재 필터에 맞는 리스트 쿼리
  const { data, isLoading } =
    filter === 'all'
      ? useNotifications({ page, size: 20 })
      : useUnreadNotifications({ page, size: 20 });

  const { markAsRead, markAllAsRead } = useNotificationActions();

  const notifications = data?.data?.content || [];
  const totalPages = data?.data?.total_pages || 0;
  const totalElements = data?.data?.total_elements || 0;

  // 독립적인 카운트 (탭과 무관하게 고정)
  const totalCount = allCountData?.data?.total_elements || 0;
  const unreadCount = unreadCountData?.data?.total_elements || 0;

  const handleFilterChange = (newFilter: 'all' | 'unread') => {
    setFilter(newFilter);
    setPage(0); // 필터 변경 시 첫 페이지로 이동
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">알림</h2>
          <p className="text-gray-500 text-sm mt-1">
            시스템 알림 및 중요 업데이트를 확인하세요
          </p>
        </div>
        <div className="flex space-x-3">
          {totalElements > 0 && filter === 'unread' && (
            <Button size="sm" variant="bordered" onPress={markAllAsRead}>
              모두 읽음 처리
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div>
        {/* Filter Buttons */}
        <div className="mb-6">
          <ButtonGroup>
            <Button
              variant={filter === 'all' ? 'solid' : 'light'}
              color={filter === 'all' ? 'primary' : 'default'}
              onPress={() => handleFilterChange('all')}
            >
              전체 ({totalCount})
            </Button>
            <Button
              variant={filter === 'unread' ? 'solid' : 'light'}
              color={filter === 'unread' ? 'primary' : 'default'}
              onPress={() => handleFilterChange('unread')}
            >
              읽지 않음 ({unreadCount})
            </Button>
          </ButtonGroup>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 text-default-400">
            <svg
              className="w-24 h-24 mb-4 opacity-50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2">알림이 없습니다</h3>
            <p className="text-sm">
              {filter === 'all'
                ? '아직 받은 알림이 없습니다'
                : '읽지 않은 알림이 없습니다'}
            </p>
          </div>
        ) : (
          /* Notification List */
          <>
            <div className="space-y-3">
              {notifications.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notification={notif}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  total={totalPages}
                  page={page + 1}
                  onChange={(p) => setPage(p - 1)}
                  showControls
                  classNames={{
                    wrapper: 'gap-2',
                    item: 'w-10 h-10 text-sm',
                    cursor: 'bg-primary text-white font-medium',
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

NotificationsPage.displayName = 'NotificationsPage';
